import { artworkCmd } from '../../services/apiClient';

/**
 * Jev's questions about a PAGE rather than a prompt (artwork.ai.understand): which Animate recipe
 * suits it, which format it is exported in, which of Flobi's template categories the file is.
 * The editor sends a few plain lines about the page (pageSummary.ts) and its own list; an answer is
 * always one of that list. Each only suggests — a card marked Suggested, a format preselected while
 * nobody has picked one, a category chip turned on once — and a slow or missing answer is none.
 */

type Surface = 'animate' | 'export' | 'templates';
interface Decided { value: string; band: 'sure' | 'offer' | 'ask'; confidence: number }
const KEY: Record<Surface, 'recipe' | 'format' | 'category'> = { animate: 'recipe', export: 'format', templates: 'category' };

/** The same page and list asked again in a visit is answered from here (Jev caches a day too). */
const asked = new Map<string, Promise<Decided | null>>();

export function askPage(
  surface: Surface,
  documentId: string,
  page: string | null | undefined,
  lists: { recipes?: { id: string; name: string }[]; categories?: readonly string[] } = {},
): Promise<Decided | null> {
  if (!page?.trim()) return Promise.resolve(null);
  const key = `${surface}|${documentId}|${page}|${JSON.stringify(lists)}`;
  let answer = asked.get(key);
  if (!answer) {
    answer = artworkCmd<Record<string, Decided | null>>('artwork.ai.understand', {
      surface,
      documentId,
      page: page.slice(0, 1500),
      ...(lists.recipes?.length ? { recipes: lists.recipes.slice(0, 40) } : {}),
      ...(lists.categories?.length ? { categories: lists.categories.slice(0, 60) } : {}),
    })
      .then((data) => data?.[KEY[surface]] ?? null)
      .catch(() => null);
    asked.set(key, answer);
    // A failed or empty answer may be asked again on the next visit to the page.
    void answer.then((value) => { if (!value) asked.delete(key); });
  }
  return answer;
}

/** Worth acting on: sure, or (for a mark that only suggests) an offer it is fairly sure of. */
export const worthIt = (d: Decided | null, offerFrom = 0.6) => !!d && (d.band === 'sure' || (d.band === 'offer' && d.confidence >= offerFrom));
